import {MeshCellValueFormat} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cellFormat/valueFormat/MeshCellValueFormat';
import {CurrentLocale} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cellSettings/valueFormatter/LocaleInfo';
import {IFunctionContainer} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/ClientFunction';
import {Function, Param} from '@mintdata/mint-kit/api/decorators/Function';
import {FunctionFeature, ISheetAPI, SheetType} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {ADate} from '@mintdata/mint-kit/api/values/ADate';
import {ADateTime} from '@mintdata/mint-kit/api/values/ADateTime';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {AInteger} from '@mintdata/mint-kit/api/values/AInteger';
import {ANumber} from '@mintdata/mint-kit/api/values/ANumber';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {EvalErrors} from '@mintdata/mint-kit/api/values/errors/EvalErrors';
import {CoercionUtils} from '@mintdata/mint-kit/impl/coercionUtils/CoercionUtils';
import {ExcelDateTimeUtil} from '@mintdata/mint-kit/impl/coercionUtils/ExcelDateTimeUtil';
import bind from 'bind-decorator';
import * as moment from 'moment';

const oneSecondMilliseconds = 1000;
const oneMinuteMilliseconds = 60 * oneSecondMilliseconds;
const oneHourMilliseconds = 60 * oneMinuteMilliseconds;
const oneDayMilliseconds = 24 * oneHourMilliseconds;

const DATE_FORMATS = ['excel', 'iso', 'unix', 'timestamp', 'date'];
const DATE_TIME_FORMATS = ['excel', 'iso', 'unix', 'timestamp', 'datetime'];

const timezoneRegex = /^utc(?:([+-])([1][0-4]|[0][0-9]|[0-9]{1})(?::(00|30|45))?)?$/;

export class DateFunctions implements IFunctionContainer
{
    // public d1900 = new Date(1900, 0, 1);
    // public d1900 = -2209075200000;

    private get _currentTimezoneOffset():number
    {
        return (new Date()).getTimezoneOffset();
    }

    private get _currentTimezoneOffsetInMilliseconds():number
    {
        return this._currentTimezoneOffset * oneMinuteMilliseconds;
    }

    public get namespace()
    {
        return 'date';
    }

    @Function({
        title: 'today()',
        alias: 'TODAY',
        example: 'TODAY("UTC-7")',
        tip: 'Returns today\'s date.',
        icon: '',
        returns:
            {
                type: Type.Date,
                title: 'Current date',
                tip: 'Returns current date, or date in the specified timezone.'
            }
    })
    @bind
    public today(@Param({
                     title: 'api',
                     tip: 'api',
                     type: SheetType.SheetAPI
                 }) api:ISheetAPI,
                 @Param({
                     title: 'utc_offset',
                     tip: 'A string representing the UTC offset to use, for example UTC+8:30.',
                     type: Type.String,
                     optional: true
                 }) timezone:AString):AInteger | AError
    {
        const cellLocation = api.getSheetContext().getCellLocation();
        api.getSheetContext().setCellFormat(cellLocation, MeshCellValueFormat.Date.id);
        return this._getCurrentDateAsExcelNumber(timezone);
    }

    @Function({
        title: 'TIME(hour, minute, second)',
        alias: 'TIME',
        example: 'TIME(11,40,59)',
        tip: 'Converts an hour, minute, and second into a time.',
        icon: '',
        returns:
            {
                type: Type.Number,
                title: 'TIME',
                tip: 'Returns the time value from specified hours, minutes and seconds.'
            }
    })
    @bind
    public time(@Param({
                    title: 'api',
                    tip: 'api',
                    type: SheetType.SheetAPI
                }) api:ISheetAPI,
                @Param({
                    title: 'hour',
                    tip: 'The hour component of the time.',
                    type: Type.Integer
                }) hours:AInteger,
                @Param({
                    title: 'minute',
                    tip: 'The minute component of the time.',
                    type: Type.Integer
                })minutes:AInteger,
                @Param({
                    title: 'second',
                    tip: 'The second component of the time.',
                    type: Type.Integer
                })seconds:AInteger,
                @Param({
                    title: 'millisecond',
                    tip: 'The millisecond component of the time.',
                    type: Type.Integer,
                    optional: true
                })milliseconds:AInteger):ANumber | AError
    {
        let ms = 0;
        if( milliseconds )
        {
            ms = milliseconds.value;
        }
        let duration = this._getDuration(hours.value, minutes.value, seconds.value, ms);

        if( duration < 0 )
            return EvalErrors.Num;
        const cellLocation = api.getSheetContext().getCellLocation();
        api.getSheetContext().setCellFormat(cellLocation, MeshCellValueFormat.Time.id);
        return ANumber.fromNumber(duration / oneDayMilliseconds);
    }

    @Function({
        title: 'DATEVALUE(date_string)',
        alias: 'DATEVALUE',
        example: 'DATEVALUE("7/20/1969")',
        tip: 'Converts a provided date string in a known format to a date value.',
        icon: '',
        returns:
            {
                type: Type.Number,
                title: 'DATEVALUE',
                tip: 'Returns a date value.'
            }
    })
    @bind
    public datevalue(@Param({
                         title: 'api',
                         tip: 'api',
                         type: SheetType.SheetAPI
                     }) api:ISheetAPI,
                     @Param({
                         title: 'date_string',
                         tip: 'The string representing the date.',
                         type: Type.String
                     }) date_string:AString,
                     @Param({
                         title: 'format',
                         tip: 'The string representing format of the date.',
                         type: Type.String,
                         optional: true
                     }) format:AString):ANumber | AError
    {
        let momentValue;
        if( format )
        {
            momentValue = moment(date_string.value, format.value);
        }
        else
        {
            momentValue = moment(date_string.value);
        }

        if( !momentValue.isValid() )
            return EvalErrors.Value;

        const dateValue = new Date(momentValue.valueOf() as number);
        return ANumber.fromNumber(this._daysFrom1900(dateValue));
    }

    @Function({
        title: 'TIMEVALUE(time_string)',
        alias: 'TIMEVALUE',
        example: 'TIMEVALUE("02:20:12 am")',
        tip: 'Returns the fraction of a 24-hour day the time represents.',
        icon: '',
        returns:
            {
                type: Type.Number,
                title: 'TIMEVALUE',
                tip: 'Returns the fraction of a 24-hour day the time represents.'
            }
    })
    @bind
    public timevalue(@Param({
                         title: 'api',
                         tip: 'api',
                         type: SheetType.SheetAPI
                     }) api:ISheetAPI,
                     @Param({
                         title: 'time_string',
                         tip: 'The string that holds the time representation.',
                         type: Type.String
                     }) time_string:AString,
                     @Param({
                         title: 'format_string',
                         tip: 'The string representing format of the date.',
                         type: Type.String,
                         optional: true
                     }) format:AString):ANumber | AError
    {
        moment.locale(CurrentLocale.locale);
        const defaultFormats = ['LTS', 'LT', 'LLLL', 'LLL', 'LL', 'L'];
        let momentValue;
        if( format )
        {
            momentValue = moment(time_string.value, format.value);
        }
        else
        {
            momentValue = moment(time_string.value, defaultFormats);
        }

        if( !momentValue.isValid() )
            return EvalErrors.Value;
        const duration = this._getDuration(momentValue.hour(), momentValue.minute(), momentValue.second());

        if( duration < 0 )
            return EvalErrors.Num;

        return ANumber.fromNumber(duration / oneDayMilliseconds);
    }

    @Function({
        title: 'DATETIMEVALUE(date_string)',
        alias: 'DATETIMEVALUE',
        example: 'DATETIMEVALUE("7/20/1969 4:00 AM")',
        tip: 'Converts a provided date string in a known format to a date value.',
        icon: '',
        returns:
            {
                type: Type.Number,
                title: 'DATETIMEVALUE',
                tip: 'Returns a date value.'
            }
    })
    @bind
    public datetimevalue(@Param({
                             title: 'api',
                             tip: 'api',
                             type: SheetType.SheetAPI
                         }) api:ISheetAPI,
                         @Param({
                             title: 'date_string',
                             tip: 'The string representing the date.',
                             type: Type.String
                         }) date_string:AString,
                         @Param({
                             title: 'format',
                             tip: 'The string representing format of the date.',
                             type: Type.String,
                             optional: true
                         }) format:AString):ANumber | AError
    {
        let momentValue;
        if( format )
        {
            momentValue = moment(date_string.value, format.value);
        }
        else
        {
            momentValue = moment(date_string.value);
        }

        if( !momentValue.isValid() )
            return EvalErrors.Value;

        const dateValue = new Date(momentValue.valueOf() as number);
        // return ANumber.fromNumber(this._daysFrom1900(dateValue) - 1 + (dateValue.getHours() * 3600 +
        return ANumber.fromNumber(this._daysFrom1900(dateValue) + (dateValue.getHours() * 3600 +
            dateValue.getMinutes() * 60 + dateValue.getSeconds()) / 86400);
    }

    @Function({
        title: 'HOUR(time)',
        alias: 'HOUR',
        example: 'HOUR("02:20:12")',
        tip: 'Returns the hour component of a specific time, in numeric format.',
        icon: '',
        returns:
            {
                type: Type.Number,
                title: 'HOUR',
                tip: 'Returns the hour as an integer for the given date value.'
            }
    })
    @bind
    public hour(@Param({
        title: 'time',
        tip: 'The time from which to calculate the hour component.',
        type: SheetType.AnyIValue
    }) time:any):ANumber | AError
    {
        if( Type.isInstanceOf(time, Type.String) )
        {
            moment.locale(CurrentLocale.locale);
            const momentValue = moment.utc((<AString>time).value, ['LTS', 'LT']);

            if( !momentValue.isValid() )
                return EvalErrors.Value;

            return ANumber.fromNumber(Math.floor(momentValue.hour()));
        }

        if( Type.isInstanceOf(time, Type.DateTime) )
        {
            return ANumber.fromNumber(Math.floor((<ADateTime>time).hours));
        }

        if( Type.isInstanceOf(time, Type.Number) || Type.isInstanceOf(time, Type.Integer) )
        {
            const totalMilliseconds = ExcelDateTimeUtil.fromExcelDuration(time.value);
            const duration = moment.duration(totalMilliseconds);
            return AInteger.fromNumber(Math.floor(duration.hours()));
        }

        return EvalErrors.Value;
    }

    @Function({
        title: 'MINUTE(time)',
        alias: 'MINUTE',
        example: 'MINUTE("02:20:12")',
        tip: 'Returns the minute component of a specific time, in numeric format.',
        icon: '',
        returns:
            {
                type: Type.Number,
                title: 'MINUTE',
                tip: 'Returns the minute as an integer for the given date value.'
            }
    })
    @bind
    public minute(@Param({
        title: 'time',
        tip: 'The time from which to calculate the minute component.',
        type: SheetType.AnyIValue
    }) time:any):ANumber | AError
    {
        if( Type.isInstanceOf(time, Type.String) )
        {
            moment.locale(CurrentLocale.locale);
            const momentValue = moment.utc((<AString>time).value, ['LTS', 'LT']);

            if( !momentValue.isValid() )
                return EvalErrors.Value;

            return ANumber.fromNumber(Math.floor(momentValue.minute()));
        }

        if( Type.isInstanceOf(time, Type.DateTime) )
        {
            return ANumber.fromNumber(Math.floor((<ADateTime>time).minutes));
        }

        if( Type.isInstanceOf(time, Type.Number) || Type.isInstanceOf(time, Type.Integer) )
        {
            const totalMilliseconds = ExcelDateTimeUtil.fromExcelDuration(time.value);
            const duration = moment.duration(totalMilliseconds);
            return AInteger.fromNumber(Math.floor(duration.minutes()));
        }

        return EvalErrors.Value;
    }

    @Function({
        title: 'SECOND(time)',
        alias: 'SECOND',
        example: 'SECOND("02:20:12")',
        tip: 'Returns the second component of a specific time, in numeric format.',
        icon: '',
        returns:
            {
                type: Type.Number,
                title: 'SECOND',
                tip: 'Returns the second as an integer for the given date value.'
            }
    })
    @bind
    public second(@Param({
        title: 'time',
        tip: 'The time from which to calculate the second component.',
        type: SheetType.AnyIValue
    }) time:any):ANumber | AError
    {
        if( Type.isInstanceOf(time, Type.String) )
        {
            moment.locale(CurrentLocale.locale);
            const momentValue = moment.utc((<AString>time).value, ['LTS', 'LT']);

            if( !momentValue.isValid() )
                return EvalErrors.Value;

            return ANumber.fromNumber(Math.floor(momentValue.second()));
        }

        if( Type.isInstanceOf(time, Type.DateTime) )
        {
            return ANumber.fromNumber(Math.floor((<ADateTime>time).seconds));
        }

        if( Type.isInstanceOf(time, Type.Number) || Type.isInstanceOf(time, Type.Integer) )
        {
            const totalMilliseconds = ExcelDateTimeUtil.fromExcelDuration(time.value);
            const duration = moment.duration(totalMilliseconds);
            return AInteger.fromNumber(Math.floor(duration.seconds()));
        }

        return EvalErrors.Value;
    }

    @Function({
        title: 'date(year,month,day)',
        alias: 'DATE',
        example: 'DATE(2018,10,11)',
        tip: 'Returns the sequential serial number that represents a particular date',
        icon: '',
        returns:
            {
                type: Type.Integer,
                title: 'Date',
                tip: 'Returns sequential serial number that represents a particular date'
            }
    })
    @bind
    public date(@Param({
                    title: 'api',
                    tip: 'api',
                    type: SheetType.SheetAPI
                }) api:ISheetAPI,
                @Param({
                    title: 'Year',
                    tip: 'Year part',
                    type: Type.Integer
                }) year:AInteger,
                @Param({
                    title: 'Month',
                    tip: 'Month part',
                    type: Type.Integer
                })month:AInteger,
                @Param({
                    title: 'Day',
                    tip: 'Day part',
                    type: Type.Integer
                })day:AInteger):AInteger | AError
    {
        const cellLocation = api.getSheetContext().getCellLocation();
        api.getSheetContext().setCellFormat(cellLocation, MeshCellValueFormat.Date.id);
        let yearValue;
        if( year.value < 0 ) return EvalErrors.Num;
        if( year.value < 1899 )
        {
            yearValue = 1900 + year.value;
        }
        else if( year.value < 9999 )
        {
            yearValue = year.value;
        }
        else
        {
            return EvalErrors.Num;
        }
        const jsdate = new Date(yearValue, month.value - 1, day.value);
        return AInteger.fromNumber(this._daysFrom1900(jsdate));
    }

    @Function({
        title: 'now()',
        tip: 'Returns today\'s date and time.',
        alias: 'NOW',
        example: 'NOW()',
        usesFeatures: [
            FunctionFeature.ModifiesSpreadsheet,
        ],
        returns:
            {
                type: Type.String,
                title: 'result',
                tip: 'Time in excel format'
            }
    })
    @bind
    public now(@Param({
        title: 'sheetAPI',
        tip: 'API',
        type: SheetType.SheetAPI
    }) api:ISheetAPI):ANumber | AError
    {
        const cellLocation = api.getSheetContext().getCellLocation();
        api.getSheetContext().setCellFormat(cellLocation, '00000000-0000-0000-0000-de4db33f2501');
        return this._getCurrentTimeAsExcelNumber();
    }

    @Function({
        title: 'timestamp',
        tip: 'Forms timestamp from Excel serial number of some day',
        alias: 'TIMESTAMP',
        example: 'TIMESTAMP(43657)',
        usesFeatures: [
            FunctionFeature.ModifiesSpreadsheet
        ],
        returns:
            {
                type: Type.Integer,
                title: 'result',
                tip: 'Timestamp'
            }
    })
    @bind
    public timestamp(
        @Param({
            title: 'sheetAPI',
            tip: 'API',
            type: SheetType.SheetAPI
        }) api:ISheetAPI,
        @Param({
            title: 'Serial',
            tip: 'Serial number',
            type: Type.Number
        }) serial:ANumber):ANumber
    {
        // const jsdate = this._parseDateFromExcelValue(serial.value);
        const jsdate = ExcelDateTimeUtil.excelDateToJsDate(serial.value);
        const cellLocation = api.getSheetContext().getCellLocation();
        api.getSheetContext().setCellFormat(cellLocation, '00000000-0000-0000-0000-de4db33f2000');
        // return ANumber.fromNumber((jsdate.getTime() + this.currentTimezoneOffsetInMilliseconds) / 1000);
        return ANumber.fromNumber(jsdate.getTime() / 1000);
    }

    @Function({
        title: 'From Timestamp',
        tip: 'Forms Excel number representing datetime from UNIX timestamp',
        alias: 'FROM_TIMESTAMP',
        example: 'FROM_TIMESTAMP(1563973775)',
        usesFeatures: [
            FunctionFeature.ModifiesSpreadsheet
        ],
        returns:
            {
                type: Type.Integer,
                title: 'result',
                tip: 'Serial number of Excel datetime'
            }
    })
    @bind
    public fromTimestamp(
        @Param({
            title: 'sheetAPI',
            tip: 'API',
            type: SheetType.SheetAPI
        }) api:ISheetAPI,
        @Param({
            title: 'Timestamp',
            tip: 'Timestamp in seconds',
            type: Type.Integer
        }) timestamp:AInteger):AInteger
    {
        const jsdate = new Date(timestamp.value * 1000);
        const cellLocation = api.getSheetContext().getCellLocation();
        api.getSheetContext().setCellFormat(cellLocation, '00000000-0000-0000-0000-de4db33f2000');
        // return new ANumber(this._daysFrom1900(jsdate) - 1 + (jsdate.getHours() * 3600 + jsdate.getMinutes() * 60 + jsdate.getSeconds()) / 86400);
        return new ANumber(this._daysFrom1900(jsdate) + (jsdate.getHours() * 3600 + jsdate.getMinutes() * 60 + jsdate.getSeconds()) / 86400);
    }

    @Function({
        title: 'parseTimestamp(dateString, [formatString])',
        tip: 'Parses date string to timestamp. Default format is YYYY-MM-DD hh:mm:ss Z' +
            'Default timeOffset is browser\'s TimeZone\'s offset',
        alias: 'PARSE_TIMESTAMP',
        example: 'PARSE_TIMESTAMP("2018-08-24 +0500", "YYYY-MM-DD hh:mm:ss Z")',
        usesFeatures: [
            FunctionFeature.ModifiesSpreadsheet
        ],
        returns:
            {
                type: Type.Integer,
                title: 'result',
                tip: 'Timestamp in milliseconds'
            }
    })
    @bind
    public parseTimestamp(
        @Param({
            title: 'dateString',
            tip: 'Date string',
            type: Type.String
        }) dateString:AString,
        @Param({
            title: 'formatString',
            tip: 'Format string',
            type: Type.String,
            optional: true
        }) formatString:AString):AInteger | AError
    {
        const date = dateString.value;
        const format = formatString ? formatString.value : 'YYYY-MM-DD hh:mm:ss Z';

        const result = moment(date, format);
        if( !result.isValid() )
            return new AError('#INVALID_DATE');

        return AInteger.fromNumber(result.valueOf());
    }

    @Function({
        title: 'month',
        tip: 'Returns the month of a date',
        alias: 'MONTH',
        example: 'MONTH(DATE(2018,10,11)) or MONTH(39846)',
        usesFeatures: [
            FunctionFeature.ModifiesSpreadsheet
        ],
        returns:
            {
                type: Type.Integer,
                title: 'result',
                tip: 'The month of a date'
            }
    })
    @bind
    public month(@Param({
                     title: 'sheetAPI',
                     tip: 'API',
                     type: SheetType.SheetAPI
                 })api:ISheetAPI,
                 @Param({
                     title: 'Date',
                     tip: 'Date. Can be reference to result of DATE function or integer representing number of days from 1.01.1900',
                     type: SheetType.AnyIValue
                 }) date:any):AInteger
    {

        const parsedDate = this._parseDate(date);
        return AInteger.fromNumber(parsedDate.getMonth() + 1);

    }

    @Function({
        title: 'DAY',
        tip: 'Returns the day of a date',
        alias: 'DAY',
        example: 'DAY(DATE(2018,10,11)) or DAY(39846)',
        usesFeatures: [
            FunctionFeature.ModifiesSpreadsheet
        ],
        returns:
            {
                type: Type.Integer,
                title: 'result',
                tip: 'The day of a date'
            }
    })
    @bind
    public day(@Param({
                   title: 'sheetAPI',
                   tip: 'API',
                   type: SheetType.SheetAPI
               })api:ISheetAPI,
               @Param({
                   title: 'Date',
                   tip: 'Date. Can be reference to result of DATE function or integer representing number of days from 1.01.1900',
                   type: SheetType.AnyIValue
               }) date:any):AInteger
    {
        const parsedDate = this._parseDate(date);
        return AInteger.fromNumber(parsedDate.getDate());

    }

    @Function({
        title: 'WEEKDAY',
        tip: 'Returns a number representing the day of the week of a date',
        alias: 'WEEKDAY',
        example: 'WEEKDAY(DATE(2018,10,11)) or WEEKDAY(39846)',
        usesFeatures: [
            FunctionFeature.ModifiesSpreadsheet
        ],
        returns:
            {
                type: Type.Integer,
                title: 'result',
                tip: 'The day of the week of a date'
            }
    })
    @bind
    public weekday(@Param({
                       title: 'sheetAPI',
                       tip: 'API',
                       type: SheetType.SheetAPI
                   })api:ISheetAPI,
                   @Param({
                       title: 'Date',
                       tip: 'Date. Can be reference to result of DATE function or integer representing number of days from 1.01.1900',
                       type: SheetType.AnyIValue
                   }) date:any,
                   @Param({
                       title: 'Type',
                       tip: `A number indicating which numbering system to use to represent weekdays. DEFAULT = 1.
If type is 1, days are counted from Sunday and the value of Sunday is 1, therefore the value of Saturday is 7.
If type is 2, days are counted from Monday and the value of Monday is 1, therefore the value of Sunday is 7.
If type is 3, days are counted from Monday and the value of Monday is 0, therefore the value of Sunday is 6.`,
                       type: Type.Number,
                       optional: true
                   }) type:ANumber):AInteger | AError
    {
        const typeValue:number = type ? type.value : 1;

        if( typeValue < 1 || typeValue > 3 )
            return EvalErrors.Num;

        const parsedDate:Date = this._parseDate(date);

        // 0 - Sunday, ..., 6 - Saturday
        const dayValue:number = parsedDate.getDay();

        switch( typeValue )
        {
            case 1:
                return AInteger.fromNumber(dayValue + 1);
            case 2:
                return AInteger.fromNumber(dayValue === 0 ? 7 : dayValue);
            case 3:
                return AInteger.fromNumber(dayValue === 0 ? 6 : dayValue - 1);
        }
    }

    @Function({
        title: 'YEAR',
        tip: 'Returns the year of a date',
        alias: 'YEAR',
        example: 'YEAR(DATE(2018,10,11)) or YEAR(39846)',
        usesFeatures: [
            FunctionFeature.ModifiesSpreadsheet
        ],
        returns:
            {
                type: Type.Integer,
                title: 'result',
                tip: 'The year of a date'
            }
    })
    @bind
    public year(@Param({
                    title: 'sheetAPI',
                    tip: 'API',
                    type: SheetType.SheetAPI
                })api:ISheetAPI,
                @Param({
                    title: 'Date',
                    tip: 'Date. Can be reference to result of DATE function or integer representing number of days from 1.01.1900',
                    type: SheetType.AnyIValue
                }) date:any):AInteger
    {

        const parsedDate = this._parseDate(date);
        return AInteger.fromNumber(parsedDate.getFullYear());
    }

    @Function({
        title: 'eomonth',
        tip: 'Returns serial number of the last day of month',
        alias: 'EOMONTH',
        example: 'EOMONTH(12345, 2)',
        usesFeatures: [
            FunctionFeature.ModifiesSpreadsheet
        ],
        returns:
            {
                type: Type.Integer,
                title: 'result',
                tip: 'Serial number of the last day of month'
            }
    })
    @bind
    public eomonth(@Param({
                       title: 'sheetAPI',
                       tip: 'API',
                       type: SheetType.SheetAPI
                   })api:ISheetAPI,
                   @Param({
                       title: 'Date',
                       tip: 'Date part',
                       type: SheetType.AnyIValue
                   }) startDate:any,
                   @Param({
                       title: 'Months',
                       tip: 'The number of months before or after start_date. A positive value for months yields a future date; a negative value yields a past date',
                       type: Type.Integer
                   }) months:AInteger):AInteger | AError
    {
        const jsDate = this._parseDate(startDate);
        return AInteger.fromNumber(this._daysFrom1900(new Date(jsDate.getFullYear(), jsDate.getMonth() + months.value + 1, 0)));

    }

    @Function({
        title: 'CURRENT_UTC_OFFSET',
        tip: 'Returns the UTC offset as a string like "UTC+8:30" for the current device (laptop, mobile, or tablet) in use.',
        alias: 'CURRENT_UTC_OFFSET',
        example: 'CURRENT_UTC_OFFSET()',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'result',
                tip: 'Resulting value'
            }
    })
    @bind
    public currentUtcOffset(@Param({
        title: 'sheetAPI',
        tip: 'API',
        type: SheetType.SheetAPI
    }) api:ISheetAPI):AString
    {
        return this._currentUtcOffset();
    }

    @Function({
        title: 'CONVERT_DATETIME',
        tip: 'Convert between different datetime formats',
        alias: 'CONVERT_DATETIME',
        example: 'CONVERT_DATETIME(43252.23, "unix")',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'result',
                tip: 'Resulting value'
            }
    })
    @bind
    public convertDateTime(@Param({
                               title: 'sheetAPI',
                               tip: 'API',
                               type: SheetType.SheetAPI
                           }) api:ISheetAPI,
                           @Param({
                               title: 'DateTime value',
                               tip: 'DateTime value',
                               type: SheetType.AnyIValue
                           }) dateTimeValue:IValue,
                           @Param({
                               title: 'To Format',
                               tip: 'To Format',
                               type: Type.String
                           }) toFormat:AString,
                           @Param({
                               title: 'From Format',
                               tip: 'From Format',
                               type: Type.String,
                               optional: true
                           }) fromFormat:AString):IValue | AError
    {
        return this._convertDateOrDateTime(dateTimeValue, fromFormat, toFormat, false);
    }

    private _convertDateOrDateTime(value:IValue, fromFormat:AString, toFormat:AString, isDate:boolean):IValue | AError
    {
        const allowedFormats = isDate ? DATE_FORMATS : DATE_TIME_FORMATS;
        const momentFormatString = isDate ? 'YYYY-MM-DD' : 'YYYY-MM-DDTHH:mm';

        const sFromFormat = fromFormat == null ? 'excel' : fromFormat.value;
        if( allowedFormats.indexOf(sFromFormat) < 0 )
            return new AError('#INVALID_FROM_FORMAT');

        const sToFormat = toFormat.value;
        if( allowedFormats.indexOf(sToFormat) < 0 )
            return new AError('#INVALID_TO_FORMAT');

        let timestamp = null;
        if( sFromFormat === 'iso' )
        {
            const coerced = CoercionUtils.coerce(value, Type.String, null);
            if( Type.isError(coerced) )
                return coerced as AError;

            const aMoment = moment.utc((coerced as AString).value, momentFormatString, true);
            if( !aMoment.isValid() )
                return new AError('#INVALID_ISO_FORMAT');

            timestamp = aMoment.toDate().getTime();
        }
        else if( sFromFormat === 'date' )
        {
            const coerced = CoercionUtils.coerce(value, Type.Date, null);
            if( Type.isError(coerced) )
                return coerced as AError;

            timestamp = (coerced as ADate).toJSDate().getTime();
        }
        else if( sFromFormat === 'datetime' )
        {
            const coerced = CoercionUtils.coerce(value, Type.DateTime, null);
            if( Type.isError(coerced) )
                return coerced as AError;

            timestamp = (coerced as ADateTime).toJSDate().getTime();
        }
        else
        {
            const coerced = CoercionUtils.coerce(value, Type.Number, null);
            if( Type.isError(coerced) )
                return coerced as AError;

            const numberValue = (coerced as ANumber).value;
            switch( sFromFormat )
            {
                case 'excel':
                    timestamp = ExcelDateTimeUtil.excelDateToJsDate(numberValue).getTime();
                    break;
                case 'unix':
                    timestamp = numberValue * 1000;
                    break;
                case 'timestamp':
                    timestamp = numberValue;
                    break;
            }
        }

        switch( sToFormat )
        {
            case 'iso':
                return new AString(
                    moment.utc(timestamp).format(momentFormatString)
                );
            case 'date':
                return ADate.fromJSDate(new Date(timestamp));
            case 'datetime':
                return ADateTime.fromJSDate(new Date(timestamp));
            case 'excel':
                return new ANumber(
                    ADateTime.fromJSDate(new Date(timestamp)).toExcelDate()
                );
            case 'unix':
                return new ANumber(Math.ceil(timestamp / 1000));
            case 'timestamp':
                return new ANumber(Math.ceil(timestamp));
        }
    }

    @Function({
        title: 'TO_UTC_AT_OFFSET',
        tip: 'Converts the date/time in first argument to the date/time at the specified UTC offset.\n' +
            '\n' +
            'If from_utc_offset is specified, uses this UTC offset instead of CURRENT_UTC_OFFSET().',
        alias: 'TO_UTC_AT_OFFSET',
        example: 'TO_UTC_AT_OFFSET(NOW(), "UTC-7")',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'result',
                tip: 'Resulting value'
            }
    })
    @bind
    public toUtcAtOffset(@Param({
                             title: 'sheetAPI',
                             tip: 'API',
                             type: SheetType.SheetAPI
                         }) api:ISheetAPI,
                         @Param({
                             title: 'date_time',
                             tip: 'A date/time value (in Excel-style format)',
                             type: SheetType.AnyIValue
                         }) dateTime:ANumber,
                         @Param({
                             title: 'to_utc_offset',
                             tip: 'The target UTC offset, a string like "UTC+8:30"',
                             type: Type.String
                         }) toTZ:AString,
                         @Param({
                             title: 'from_utc_offset',
                             tip: 'The UTC offset to use instead of the CURRENT_UTC_OFFSET(), a string like "UTC+8:30"',
                             type: Type.String,
                             optional: true
                         }) fromTZ:AString):ANumber | AError
    {
        return this._toUtcAtOffset(dateTime, toTZ, fromTZ);
    }

    private _toUtcAtOffset(value:IValue, toTZ:AString, fromTZ:AString = null):ANumber | AError
    {
        const toDelta:ANumber | AError = this._getTimezoneDelta(toTZ);

        if( Type.isError(toDelta) )
            return new AError('#INVALID_TO_UTC_OFFSET');

        let timestamp:number;

        let fromDelta:ANumber | AError = this._getTimezoneDelta(fromTZ);

        if( fromTZ )
        {
            fromDelta = this._getTimezoneDelta(fromTZ);

            if( Type.isError(fromDelta) )
                return new AError('#INVALID_FROM_UTC_OFFSET');
        }
        else
        {
            const currentUtcOffset:AString = this._currentUtcOffset();
            fromDelta = this._getTimezoneDelta(currentUtcOffset);
        }

        const coerced = CoercionUtils.coerce(value, Type.DateTime, null);
        if( Type.isError(coerced) )
            return coerced as AError;

        timestamp = (coerced as ADateTime).toJSDate().getTime();

        const delta:number = (fromDelta as ANumber).value - (toDelta as ANumber).value;

        timestamp -= delta;

        return ANumber.fromNumber(
            ADateTime.fromJSDate(new Date(timestamp)).toExcelDate()
        );
    }

    private _getDuration(hours:number, minutes:number, seconds:number, milliseconds:number = 0)
    {
        const duration = (hours * oneHourMilliseconds) + (minutes * oneMinuteMilliseconds) + (seconds * oneSecondMilliseconds + milliseconds);
        return duration;
    }

    private _getCurrentTimeAsExcelNumber(timezone?:AString):ANumber | AError
    {
        // const timezoneDelta:ANumber | AError = this._getTimezoneDelta(timezone) as ANumber;

        // if( Type.isError(timezoneDelta) )
        //     return timezoneDelta;

        // const timeNow:Date = new Date(Date.now() + timezoneDelta.value);
        const timeNow:Date = new Date(Date.now());

        // -1 here because day 0 for excel is 31.12.1899. d1900 is a day 1
        // return new ANumber(this._daysFrom1900(now) - 1 + (now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds()) / 86400);
        // return new ANumber(this._daysFrom1900(now) + (now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds()) / 86400);
        return ANumber.fromNumber(this._dayAndTimeFrom1900(timeNow));
    }

    private _getCurrentDateAsExcelNumber(timezone?:AString):ANumber | AError
    {
        const timezoneDelta:ANumber | AError = this._getTimezoneDelta(timezone) as ANumber;

        if( Type.isError(timezoneDelta) )
            return timezoneDelta;

        const timeNow:Date = new Date(Date.now() + timezoneDelta.value);
        // -1 here because day 0 for excel is 31.12.1899. d1900 is a day 1
        // return new ANumber(this._daysFrom1900(now) - 1);
        return ANumber.fromNumber(this._daysFrom1900(timeNow));
    }

    private _getTimezoneDelta(timezone:AString):ANumber | AError
    {
        if( !timezone ) return ANumber.fromNumber(0);

        const timezoneMatch = timezone.value.toLowerCase().match(timezoneRegex);

        if( !timezoneMatch )
            return new AError('#INVALID_TIMEZONE');

        let sign:string | number = timezoneMatch[1];
        const timezoneHours:number = parseInt(timezoneMatch[2]) || 0;
        const timezoneMinutes:number = parseInt(timezoneMatch[3]) || 0;

        // const hoursWith30 = [-9, -3, -2, 3, 4, 5, 6, 9, 10];
        // const hoursWith45 = [5, 8, 12, 13];

        sign = sign ? sign === '-' ? -1 : sign === '+' ? 1 : 1 : 1;

        const result = this._currentTimezoneOffsetInMilliseconds + (sign as number) *
            (timezoneHours * oneHourMilliseconds + timezoneMinutes * oneMinuteMilliseconds);

        return ANumber.fromNumber(result);
    }

    private _currentUtcOffset():AString
    {
        const tzo = this._currentTimezoneOffset;

        const tzSign:number = Math.sign(tzo);
        const tzHours:number = Math.ceil(tzSign * tzo / 60);
        const tzMinutes:number = tzSign * tzo % 60;

        return AString.fromString(`UTC${tzSign <= 0 ? '+' : '-'}${tzHours}${tzMinutes ? tzMinutes : ''}`);
    }

    private _daysFrom1900(date:Date):number
    {
        // const dateValue = date.valueOf();
        // const d1900Value = this.d1900.valueOf();
        // const diff = dateValue - d1900Value;
        // const result = Math.ceil(diff / oneDayMilliseconds);
        //
        // const addOn = (result < 59) ? 1 : 2; // 60 - 2/29/1900 with 3/1/1900
        //
        // return result + addOn;

        // https://stackoverflow.com/questions/16229494/converting-excel-date-serial-number-to-date-using-javascript
        const returnDateTime = this._dayAndTimeFrom1900(date);

        return parseInt(returnDateTime.toString().substr(0, 5));
    }

    private _dayAndTimeFrom1900(date:Date):number
    {
        // https://stackoverflow.com/questions/16229494/converting-excel-date-serial-number-to-date-using-javascript
        const returnDateTime = 25569.0 + ((date.getTime() - (date.getTimezoneOffset() * oneMinuteMilliseconds)) / oneDayMilliseconds);

        return returnDateTime;
    }

    private _parseDate(date:any):Date
    {
        if( date instanceof Date )
        {
            return new Date(date);
        }
        if( Type.isInstanceOf(date, Type.Date) )
        {
            return (date as ADate).toJSDate();
        }
        if( Type.isInstanceOf(date, Type.String) )
        {
            moment.locale(CurrentLocale.locale);
            const momentValue = moment.utc((date as AString).value);
            return new Date(momentValue.valueOf());
        }
        if( Type.isInstanceOf(date, Type.Integer) || Type.isInstanceOf(date, Type.Number) )
        {
            const d = parseInt(date.toString(), 10);
            // return this._parseDateFromExcelValue(d);
            return ExcelDateTimeUtil.excelDateToJsDate(d);
        }
    }

    // private _parseDateFromExcelValue(date:number):Date
    // {
    //     const addOn = (date < 59) ? 1 : 2;  // 60 - 2/29/1900 with 3/1/1900
    //
    //     const d1900Milliseconds = this.d1900.valueOf();
    //     const dateMilliseconds = (date - addOn) * oneDayMilliseconds;
    //
    //     return new Date(d1900Milliseconds + dateMilliseconds);
    // }
}
